import { Injectable } from '@angular/core';
import * as moment from 'moment';

import {
  COLORS,
  CRM_ADMIN_ROLE,
  DataTypes,
  Toast,
  VahicleAssignmentStatus,
} from '../data/constants';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/service/auth.service';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import html2canvas from 'html2canvas';
import { LookupsService } from './lookups.service';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import {
  FilteredUserTeamData,
  LookUpElement,
} from '../models/common-data.model';
import { GlobalFilterService } from './global-filter.service';

@Injectable({
  providedIn: 'root',
})
export class HelpersService {
  dataType = DataTypes;
  destroy: ReplaySubject<any> = new ReplaySubject<any>(1);

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private lookupsService: LookupsService,
    private globalFilterService: GlobalFilterService
  ) {}

  getDateFromDbFormatted(dateInUtc: any) {
    return moment(dateInUtc).local().format('YYYY-MM-DDTHH:mm:ssZ');
  }

  setDateFormatted(dateInLocal: Date) {
    if (dateInLocal) {
      return moment(dateInLocal).format('YYYY-MM-DDTHH:mm:ssZ');
    } else {
      return '';
    }
  }

  getDateFromDb(dateInUtc: any) {
    return dateInUtc ? moment(dateInUtc).local() : null;
  }

  getTimeFromDb(dateInUtc: any) {
    return moment(dateInUtc).local().format('hh:mm A');
  }

  getDbTime(time: any) {
    let utcTime = moment(time, 'HH:mm').format('hh:mm A');
    return utcTime;
  }

  getTimeFromDateTime(datetime: any) {
    return moment(datetime).format('hh:mm A');
  }

  getDateInputFormatted(dateInUtc: any) {
    return moment(dateInUtc).format('YYYY-MM-DDTHH:mm:ssZ');
  }

  setDateForDb(dateInLocal: any) {
    if (dateInLocal) {
      return moment.utc(dateInLocal).format('YYYY-MM-DDTHH:mm:ssZ');
    } else {
      return null;
    }
  }

  setDateTime(date: any, time: any) {
    let utcDate = moment(date).utc().format('DD-MM-YYYY');
    return utcDate + ' ' + time;
  }

  getDateFromDateTime(dateInUtc: any) {
    return moment(dateInUtc).format('YYYY-MM-DD');
  }

  getFormattedLocalDate(date: any) {
    return date
      ? moment(date, 'M/D/YYYY h:mm:ss A Z').format('MM/DD/YYYY')
      : null;
  }

  showErrorToast(msg: string) {
    Toast.fire({
      icon: 'error',
      title: msg,
    });
  }
  showSuccessToast(msg: string) {
    Toast.fire({
      icon: 'success',
      title: msg,
    });
  }

  showCreateSuccessToastWithMessage(msg: string) {
    Toast.fire({
      icon: 'success',
      title:
        this.translateService.instant('COMMON.CREATED_SUCCESSFULLY') +
        '<br/>' +
        msg,
    });
  }

  showCreateSuccessToast() {
    Toast.fire({
      icon: 'success',
      title: this.translateService.instant('COMMON.CREATED_SUCCESSFULLY'),
    });
  }

  showUpdateSuccessToast() {
    Toast.fire({
      icon: 'success',
      title: this.translateService.instant('COMMON.UPDATED_SUCCESSFULLY'),
    });
  }

  showSuccessToastWithMessage(message: string) {
    Toast.fire({
      icon: 'success',
      title: this.translateService.instant(message),
    });
  }

  showErrorToastWithMessage(message: string) {
    Toast.fire({
      icon: 'error',
      title: this.translateService.instant(message),
    });
  }

  showDeleteSuccessToast() {
    Toast.fire({
      icon: 'success',
      title: this.translateService.instant('COMMON.DELETED_SUCCESSFULLY'),
    });
  }

  getRandomColor(): string {
    return COLORS[(Math.random() * COLORS.length) | 0].toString();
  }

  getVehicleAssignmentStatusFromDb(status: string) {
    for (let i = 0; i < VahicleAssignmentStatus.length; i++) {
      const element = VahicleAssignmentStatus[i];
      if (element.value == status) {
        return element.name;
      }
    }
  }

  setDVehicleAssignmentStatusToDb(name: string) {
    for (let i = 0; i < VahicleAssignmentStatus.length; i++) {
      const element = VahicleAssignmentStatus[i];
      if (element.name == name) {
        return element.value;
      }
    }
  }

  toUpperCase(str: string) {
    str = str ? str.charAt(0).toUpperCase() + str.substr(1).toLowerCase() : '';

    return str;
  }

  checkUserRole(role: string) {
    return (
      this.authService.currentUserValue?.role?.includes(role) ||
      this.authService.currentUserValue?.role?.includes(CRM_ADMIN_ROLE)
    );
  }

  preventE(event) {
    if (event.which === 101 || event.which === 69) {
      event.preventDefault();
    }
  }

  preventEAndDot(event) {
    if (event.which === 101 || event.which === 69 || event.which === 46) {
      event.preventDefault();
    }
  }

  getDateWithZeroTime(date: any) {
    return moment(date).startOf('day').format('YYYY-MM-DDT00:00:00+00:00');
  }

  addOneDay(date: string): string {
    return moment(date).add(1, 'days').format('YYYY-MM-DDTHH:mm:ssZ');
  }

  maxNumberLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.toString().length > maxLength) {
        return {
          maxlength: {
            requiredLength: maxLength,
            actualLength: value.toString().length,
          },
        };
      }
      return null;
    };
  }

  downloadPng(fileName: string, id: string) {
    const captureElement = document.querySelector('#' + id) as HTMLElement;

    html2canvas(captureElement, {
      backgroundColor: null, // Set the background color to null to make it transparent
      scale: 2, // Increase the scale to improve quality
      useCORS: true, // Enable CORS if loading images from different origins
    }).then((canvas) => {
      const imageData = canvas.toDataURL('image/png');

      const link = document.createElement('a');
      link.setAttribute('download', fileName);
      link.setAttribute('href', imageData);
      link.click();
      this.showSuccessToastWithMessage('COMMON.SUCCESSFULLY_DOWNLOADED');
    });
  }

  isCRMAdmin(role: string) {
    return this.authService?.currentUserValue?.role?.includes(CRM_ADMIN_ROLE);
  }

  confirmPasswordValidator(passwordField: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control?.parent?.get(passwordField)?.value;
      const confirmPassword = control?.value;

      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  getLocalCurrency() {
    let localCurrency: string;
    localCurrency = JSON.parse(
      localStorage.getItem('currentCompany')
    )?.localCurrency;
    return localCurrency;
  }

  getDynamicFieldConfigurationStatus(): boolean {
    const status = localStorage.getItem('dynamicFieldConfigurationStatus');
    return status === 'true';
  }

  updateCustomDataCategories(
    formGroup: FormGroup,
    customDataSourceId: string,
    destroy$: Subject<void>
  ): void {
    if (customDataSourceId) {
      this.lookupsService
        .getCustomDataCategoriesLookup(customDataSourceId)
        .pipe(takeUntil(destroy$))
        .subscribe((customDataCategories: LookUpElement[]) => {
          formGroup.patchValue({ CustomDataCategories: customDataCategories });
        });
    }
  }

  getSubCategoriesLookUpByDataCategoryIds(
    formGroup: FormGroup,
    dataCategoryIds: string[],
    destroy$: Subject<void>
  ) {
    this.lookupsService
      .getSubCategoriesLookUpByDataCategoryIds(dataCategoryIds)
      .pipe(takeUntil(destroy$))
      .subscribe((CustomDataSubCategories: LookUpElement[]) => {
        formGroup.patchValue({
          CustomDataSubCategories: CustomDataSubCategories,
        });
      });
  }

  getSubCategoryClassificationLookUpBySubDataCategoryIds(
    formGroup: FormGroup,
    subCategoryIds: string[],
    destroy$: Subject<void>
  ) {
    this.lookupsService
      .getSubCategoryClassificationLookUpBySubDataCategoryIds(subCategoryIds)
      .pipe(takeUntil(destroy$))
      .subscribe((SubCategoryClassifications: LookUpElement[]) => {
        formGroup.patchValue({
          SubCategoryClassifications: SubCategoryClassifications,
        });
      });
  }

  formatValueByDataType(dataType: string, value: any): any {
    switch (dataType) {
      case this.dataType.DATE:
        return value ? this.setDateForDb(value) : null;

      case this.dataType.BOOLEAN:
        return (value ?? false).toString();

      case this.dataType.CUSTOM:
        return value ? value : '';

      default:
        return (value ?? '').toString();
    }
  }

  getControlValue(field: any): any {
    switch (field.dataType) {
      case this.dataType.BOOLEAN:
        return field.value === 'true';
      case this.dataType.DATE:
        return field.value ? this.getDateFromDbFormatted(field.value) : null;
      case this.dataType.NUMBER:
        return Number(field.value);
      default:
        return field.value ?? '';
    }
  }

  setfilteredUserTeamData(userTeamData: FilteredUserTeamData[]) {
    localStorage.setItem('filteredUserTeamData', JSON.stringify(userTeamData));
  }

  getfilteredUserTeamData() {
    return JSON.parse(localStorage.getItem('filteredUserTeamData'));
  }

  getSelectedUserIds(): string[] {
    var selectedTeamUserIds =
      this.globalFilterService
        .getSelectedTeamUser()
        ?.reduce<string[]>(
          (acc, team) => acc.concat(team?.userIds ?? []),
          []
        ) ?? [];

    var selectedPrivilegedUserIds =
      this.globalFilterService.getSelectedPrivilegedUserIds() ?? [];

    const selectedUserIds = [
      ...new Set([...selectedTeamUserIds, ...selectedPrivilegedUserIds]),
    ];

    return selectedUserIds;
  }

  capitalizeLabel(label: string): string {
    if (!label) return '';
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  }

  getSelectedTeamUserIds() {
    var selectedTeamUserIds =
      this.globalFilterService
        .getSelectedTeamUser()
        ?.reduce<string[]>(
          (acc, team) => acc.concat(team?.userIds ?? []),
          []
        ) ?? [];

    return selectedTeamUserIds;
  }

  getCompanyId() {
    var company = localStorage.getItem('currentCompany');
    return company ? JSON.parse(company)?.id : '';
  }

  getExternalRequestStatus() {
    const data = localStorage.getItem('currentCompany');
    return data ? JSON.parse(data)?.isExternalRequestEnable : false;
  }

  getVirtualUserId() {
    const data = localStorage.getItem('currentCompany');
    return data ? JSON.parse(data)?.virtualUserId : null;
  }

  getCurrentUserId() {
    const data = localStorage.getItem('currentUser');
    return data ? JSON.parse(data)?.id : null;
  }

  isShowExternalInquiry() {
    const isExternalRequestEnable = this.getExternalRequestStatus();
    const virtualUserId = this.getVirtualUserId();
    const currentUserId = this.getCurrentUserId();

    return (
      currentUserId &&
      currentUserId === virtualUserId &&
      isExternalRequestEnable
    );
  }

  getLang() {
    return localStorage.getItem('lang') ?? 'en';
  }

  noDuplicateNamesValidator(
    formArray: AbstractControl
  ): ValidationErrors | null {
    if (!(formArray instanceof FormArray)) return null;

    const names = new Set();
    const secondaryNames = new Set();
    let hasDuplicate = false;

    formArray.controls.forEach((control) => {
      const name = control.get('Name')?.value?.trim()?.toLowerCase();
      const secondaryName = control
        .get('SecondaryName')
        ?.value?.trim()
        ?.toLowerCase();

      if (name) {
        if (names.has(name)) {
          control.get('Name')?.setErrors({ duplicate: true });
          hasDuplicate = true;
        } else {
          names.add(name);
          // remove previous error if fixed
          const errors = control.get('Name')?.errors;
          if (errors && errors['duplicate']) {
            delete errors['duplicate'];
            if (Object.keys(errors).length === 0) {
              control.get('Name')?.setErrors(null);
            } else {
              control.get('Name')?.setErrors(errors);
            }
          }
        }
      }

      if (secondaryName) {
        if (secondaryNames.has(secondaryName)) {
          control.get('SecondaryName')?.setErrors({ duplicate: true });
          hasDuplicate = true;
        } else {
          secondaryNames.add(secondaryName);
          const errors = control.get('SecondaryName')?.errors;
          if (errors && errors['duplicate']) {
            delete errors['duplicate'];
            if (Object.keys(errors).length === 0) {
              control.get('SecondaryName')?.setErrors(null);
            } else {
              control.get('SecondaryName')?.setErrors(errors);
            }
          }
        }
      }
    });

    return hasDuplicate ? { duplicatesFound: true } : null;
  }
}
